import React from 'react';
import { X, Save, Upload, RefreshCw, Eye, EyeOff } from 'lucide-react';

const BlogPostEditor = ({ 
  editingPost, 
  setEditingPost, 
  isCreating, 
  onSave, 
  onClose, 
  onImageUpload, 
  uploading 
}) => {
  if (!editingPost && !isCreating) return null;

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-800">
          {isCreating ? 'Create New Post' : 'Edit Post'}
        </h2>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg"
          data-testid="close-editor-btn"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* English Content */}
        <div className="space-y-4">
          <h3 className="font-medium text-gray-700 border-b pb-2">English</h3>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Title</label>
            <input
              type="text"
              value={editingPost?.title || ''}
              onChange={(e) => setEditingPost(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Blog post title"
              data-testid="post-title-en"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Excerpt</label>
            <textarea
              value={editingPost?.excerpt || ''}
              onChange={(e) => setEditingPost(prev => ({ ...prev, excerpt: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              rows={3}
              placeholder="Short description..."
              data-testid="post-excerpt-en"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Category</label>
            <input
              type="text"
              value={editingPost?.category || ''}
              onChange={(e) => setEditingPost(prev => ({ ...prev, category: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="e.g. Beaches, Adventures"
              data-testid="post-category-en"
            />
          </div>
        </div>

        {/* Spanish Content */}
        <div className="space-y-4">
          <h3 className="font-medium text-gray-700 border-b pb-2">Spanish</h3>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Título</label>
            <input
              type="text"
              value={editingPost?.titleEs || ''}
              onChange={(e) => setEditingPost(prev => ({ ...prev, titleEs: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Título del blog"
              data-testid="post-title-es"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Extracto</label>
            <textarea
              value={editingPost?.excerptEs || ''}
              onChange={(e) => setEditingPost(prev => ({ ...prev, excerptEs: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              rows={3}
              placeholder="Descripción corta..."
              data-testid="post-excerpt-es"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Categoría</label>
            <input
              type="text"
              value={editingPost?.categoryEs || ''}
              onChange={(e) => setEditingPost(prev => ({ ...prev, categoryEs: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="ej. Playas, Aventuras"
              data-testid="post-category-es"
            />
          </div>
        </div>

        {/* French Content */}
        <div className="space-y-4">
          <h3 className="font-medium text-gray-700 border-b pb-2">French</h3>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Titre</label>
            <input
              type="text"
              value={editingPost?.titleFr || ''}
              onChange={(e) => setEditingPost(prev => ({ ...prev, titleFr: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Titre du blog"
              data-testid="post-title-fr"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Extrait</label>
            <textarea
              value={editingPost?.excerptFr || ''}
              onChange={(e) => setEditingPost(prev => ({ ...prev, excerptFr: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              rows={3}
              placeholder="Description courte..."
              data-testid="post-excerpt-fr"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Catégorie</label>
            <input
              type="text"
              value={editingPost?.categoryFr || ''}
              onChange={(e) => setEditingPost(prev => ({ ...prev, categoryFr: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="ex. Plages, Aventures"
              data-testid="post-category-fr"
            />
          </div>
        </div>

        {/* Image Upload */}
        <div className="space-y-4">
          <h3 className="font-medium text-gray-700 border-b pb-2">Image</h3>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Featured Image</label>
            {editingPost?.image && (
              <div className="mb-3">
                <img 
                  src={editingPost.image} 
                  alt="Preview" 
                  className="w-full h-32 object-cover rounded-lg"
                />
              </div>
            )}
            <div className="flex gap-2">
              <label className="flex-1">
                <div className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-teal-500 transition-colors ${uploading ? 'opacity-50' : ''}`}>
                  {uploading ? (
                    <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
                  ) : (
                    <Upload className="w-5 h-5 text-gray-400" />
                  )}
                  <span className="text-sm text-gray-500">
                    {uploading ? 'Uploading...' : 'Upload Image'}
                  </span>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={onImageUpload}
                  className="hidden"
                  disabled={uploading}
                  data-testid="image-upload-input"
                />
              </label>
            </div>
            <div className="mt-2">
              <input
                type="text"
                value={editingPost?.image || ''}
                onChange={(e) => setEditingPost(prev => ({ ...prev, image: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Or paste image URL..."
                data-testid="post-image-url"
              />
            </div>
          </div>

          {/* Published Toggle */}
          <div className="flex items-center gap-3 pt-4">
            <button
              onClick={() => setEditingPost(prev => ({ ...prev, published: !prev.published }))}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                editingPost?.published 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-100 text-gray-600'
              }`}
              data-testid="toggle-published-btn"
            >
              {editingPost?.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              {editingPost?.published ? 'Published' : 'Draft'}
            </button>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
        <button
          onClick={onClose}
          className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          data-testid="cancel-edit-btn"
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          className="flex items-center gap-2 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          data-testid="save-post-btn"
        >
          <Save className="w-4 h-4" />
          {isCreating ? 'Create Post' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default BlogPostEditor;
